import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaShieldAlt } from 'react-icons/fa';
import BrandLogo from '../components/BrandLogo';
import '../styles/Login.css';

const RESEND_COOLDOWN_SECONDS = 30;

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Set once the password step succeeds but the account requires a code
  // emailed at login time; the form below switches to the code-entry step.
  const [twoFactor, setTwoFactor] = useState(null); // { userId, email }
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const { login, completeTwoFactorLogin, resendTwoFactorLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || sessionStorage.getItem('pendingEventRedirect') || '/dashboard';

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: '' }));
    if (error) setError('');
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) errors.email = "L'e-mail est requis";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = "L'e-mail est invalide";
    if (!formData.password) errors.password = 'Le mot de passe est requis';
    else if (formData.password.length < 6) errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        sessionStorage.removeItem('pendingEventRedirect');
        navigate(from, { replace: true });
      } else if (result.twoFactorRequired) {
        setTwoFactor({ userId: result.userId, email: result.email });
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setError(result.error || 'E-mail ou mot de passe invalide');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');

    if (code.length !== 6) {
      setError('Saisissez le code à 6 chiffres reçu par e-mail.');
      return;
    }

    setLoading(true);
    try {
      const result = await completeTwoFactorLogin(twoFactor.userId, code);
      if (result.success) {
        sessionStorage.removeItem('pendingEventRedirect');
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Code invalide ou expiré.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError('');
    const result = await resendTwoFactorLogin(twoFactor.userId);
    if (result.success) {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } else {
      setError(result.error || "Impossible de renvoyer le code pour l'instant.");
    }
  };

  return (
    <div className="login-page">
      {/* Branding Side */}
      <div className="login-branding">
        <div className="branding-content">
          <h1 className="brand-title"><BrandLogo className="login-brand-logo" /></h1>
          <p className="brand-subtitle">Retrouvez-vous dans chaque moment grâce à la reconnaissance faciale par IA</p>
          
          <div className="features-list">
            <div className="feature-item">
              <FaCheckCircle className="feature-icon" />
              <span>Reconnaissance faciale par IA</span>
            </div>
            <div className="feature-item">
              <FaCheckCircle className="feature-icon" />
              <span>Découverte instantanée de photos</span>
            </div>
            <div className="feature-item">
              <FaCheckCircle className="feature-icon" />
              <span>Sécurisé et privé</span>
            </div>
          </div>
        </div>
        <div className="branding-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
      </div>

      {/* Form Side */}
      <div className="login-form-container">
        <div className="login-form-wrapper">
          {!twoFactor ? (
            <>
              <div className="form-header">
                <h2>Bon retour</h2>
                <p>Connectez-vous pour accéder à votre compte</p>
              </div>

              {error && (
                <div className="alert alert-error">
                  <span className="alert-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <form className="login-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">Adresse e-mail</label>
                  <div className={`input-wrapper ${fieldErrors.email ? 'error' : ''}`}>
                    <FaEnvelope className="input-icon" />
                    <input type="email" id="email" name="email" placeholder="you@example.com"
                      value={formData.email} onChange={handleChange} disabled={loading} />
                  </div>
                  {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Mot de passe</label>
                  <div className={`input-wrapper ${fieldErrors.password ? 'error' : ''}`}>
                    <FaLock className="input-icon" />
                    <input type={showPassword ? 'text' : 'password'} id="password" name="password"
                      placeholder="Saisissez votre mot de passe" value={formData.password} onChange={handleChange} disabled={loading} />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
                </div>

                <div className="form-options">
                  <label className="checkbox-label">
                    <input type="checkbox" />
                    <span>Se souvenir de moi</span>
                  </label>
                  <Link to="/forgot-password" className="forgot-link">Mot de passe oublié ?</Link>
                </div>

                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? <><span className="spinner"></span>Connexion...</> : 'Se connecter'}
                </button>

                <div className="divider"><span>ou</span></div>

                <div className="form-footer">
                  <p>Vous n'avez pas de compte ? <Link to="/register" state={{ from: { pathname: from } }} className="register-link">Créer un compte</Link></p>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="form-header">
                <FaShieldAlt style={{ fontSize: '2rem', color: 'var(--primary-hover)', marginBottom: '0.75rem' }} />
                <h2>Vérification en deux étapes</h2>
                <p>
                  Saisissez le code à 6 chiffres envoyé à <strong>{twoFactor.email}</strong>
                </p>
              </div>

              {error && (
                <div className="alert alert-error">
                  <span className="alert-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <form className="login-form" onSubmit={handleVerifyCode}>
                <div className="form-group">
                  <label htmlFor="two-factor-code">Code de vérification</label>
                  <div className="input-wrapper">
                    <input
                      id="two-factor-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="123456"
                      style={{ textAlign: 'center', fontSize: '1.3rem', letterSpacing: '0.4rem' }}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                </div>

                <button type="submit" className="btn-submit" disabled={loading || code.length !== 6}>
                  {loading ? <><span className="spinner"></span>Vérification...</> : 'Vérifier'}
                </button>

                <div className="form-footer">
                  <p>
                    {resendCooldown > 0 ? (
                      <span>Renvoyer le code dans {resendCooldown}s</span>
                    ) : (
                      <button type="button" className="register-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={handleResendCode}>
                        Renvoyer le code
                      </button>
                    )}
                    {' · '}
                    <button
                      type="button"
                      className="register-link"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      onClick={() => { setTwoFactor(null); setCode(''); setError(''); }}
                    >
                      Changer de compte
                    </button>
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
